import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Quote, Heart } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import BrandMarquee from '@/components/BrandMarquee';
import { site, customerStats } from '@/data/content';

const values = [
  { icon: 'Target', title: 'Customer first', text: 'Every feature is built for support teams and the customers they serve.' },
  { icon: 'Zap', title: 'Ship fast', text: 'We release constantly so your team can solve problems today, not next quarter.' },
  { icon: 'Shield', title: 'Built on trust', text: 'Privacy, uptime, and honest pricing are non-negotiables.' },
  { icon: 'Globe', title: 'Global from day one', text: 'African roots, worldwide reach, every major channel covered.' },
];

const milestones = [
  { year: '2026', text: 'Bahati Asher Faith and his wife start ZuriHub after years of overpaying for support tools.' },
  { year: '2026', text: 'The first workspace for affordable, omnichannel African customer experience ships.' },
  { year: '2027', text: '250 businesses are using ZuriHub in its first year.' },
];

const founderPhoto = 'https://media.getzurihub.com/Jayrah%20Assets/Bahati%20Asher%20Faith.jpeg';

export default function About() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `About ${site.name}`,
    description: `What we do and why we do it at ${site.name}.`,
    url: `${site.baseUrl}/about`,
  };

  return (
    <>
      <SEO
        title="About ZuriHub - Built for African Businesses and Global Teams"
        description="Learn about ZuriHub's mission to provide affordable, enterprise-grade customer experience tools for African businesses and emerging markets. Founded in 2026 by Bahati Asher Faith after experiencing expensive support tools firsthand."
        path="/about"
        jsonLd={[jsonLd]}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-28 pb-20 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-6xl">
            One platform for every <span className="gradient-text">customer conversation</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
            {site.name} replaces a mess of support tools with a single, modern customer experience platform.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Start free <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/careers"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-7 py-3 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Join the team <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="bg-slate-50 py-24" aria-labelledby="story-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10">
              <Quote className="h-8 w-8 text-brand-300" />
              <p className="mt-4 text-xl font-bold leading-relaxed text-slate-900 sm:text-2xl">
                After three years and over $50,000 on Zendesk and Intercom, my wife and I wanted a support tool that just worked, without draining our budget.
              </p>
              <div className="mt-6 flex items-center gap-4">
                <img
                  src={founderPhoto}
                  alt="Bahati Asher Faith"
                  className="h-16 w-16 rounded-full border-4 border-brand-50 object-cover shadow-sm"
                  loading="lazy"
                />
                <div>
                  <p className="font-bold text-slate-900">Bahati Asher Faith</p>
                  <p className="text-sm text-slate-600">Founder, ZuriHub</p>
                </div>
              </div>
            </div>
            <div>
              <h2 id="story-heading" className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                Built out of real need
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                In 2026, Bahati Asher Faith and his wife started ZuriHub to give every business, especially in Africa and emerging markets, the same enterprise-grade support tools at a price that makes sense.
              </p>
              <p className="mt-4 text-lg text-slate-600">
                One year later, ZuriHub serves 250 businesses. We keep building because we have lived the problem we are solving.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                {['Affordable', 'Omnichannel', 'AI-first', 'African roots', 'Closer to you', 'Closer to your customers'].map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
                    <Heart className="h-3.5 w-3.5" /> {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="mission-heading">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <h2 id="mission-heading" className="section-title">Our mission</h2>
          <p className="mt-6 text-2xl font-bold leading-relaxed text-slate-900 sm:text-3xl">
            Make world-class customer support affordable for every business, everywhere.
          </p>
          <p className="section-subtitle mx-auto mt-4">
            We unify every conversation into one simple, AI-powered workspace so teams can focus on customers, not tools.
          </p>
        </div>
      </section>

      <section className="py-24" aria-labelledby="what-we-do-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="what-we-do-heading" className="section-title">What we do</h2>
            <p className="section-subtitle mx-auto mt-4">
              We build the customer experience platform small and growing teams actually want to use.
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {[
              { title: 'Unify channels', text: 'WhatsApp, Instagram, Telegram, Messenger, email, live chat, and voice in one inbox.' },
              { title: 'Power with AI', text: 'Zyra drafts replies, detects intent, and coaches agents in real time.' },
              { title: 'Keep it simple', text: 'No bloat, no hidden fees. One workspace, one price, one place to focus.' },
            ].map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="promise-heading">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <h2 id="promise-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">Our promise</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {[
              { title: 'Honest pricing', text: 'Clear per-agent pricing with no surprise add-ons.' },
              { title: 'Human support', text: 'Real help from real people, even on the free plan.' },
              { title: 'Built in public', text: 'We ship fast, listen to feedback, and share our roadmap.' },
            ].map((p) => (
              <div key={p.title} className="rounded-2xl border border-slate-800 bg-slate-800/50 p-6">
                <h3 className="font-bold text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-300">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="why-choose-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="why-choose-heading" className="section-title">Why choose ZuriHub</h2>
            <p className="section-subtitle mx-auto mt-4">
              Built for modern teams who care about customer experience without the enterprise price tag.
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: 'Enterprise features, startup price', text: 'Get the same powerful tools used by big companies, at a fraction of the cost.' },
              { title: 'Built for Africa, works everywhere', text: 'Optimized for African businesses with global reach across all major channels.' },
              { title: 'AI that actually helps', text: 'Zyra AI drafts replies, summarizes conversations, and coaches your team in real-time.' },
              { title: 'No hidden costs', text: 'Transparent pricing with per-agent plans. No surprise add-ons or usage limits.' },
              { title: 'Setup in minutes', text: 'Get started with your first workspace in under 10 minutes. No complex integrations required.' },
              { title: 'Real human support', text: 'Talk to real people who care about your success, even on the free plan.' },
            ].map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="milestones-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="milestones-heading" className="mb-10 text-center section-title">Our story in three steps</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {milestones.map((m) => (
              <div
                key={m.year + m.text}
                className="rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <span className="text-3xl font-extrabold text-brand-700">{m.year}</span>
                <p className="mt-2 text-sm font-semibold text-slate-700">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" aria-labelledby="values-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="values-heading" className="section-title">How we build</h2>
            <p className="section-subtitle mx-auto">Four principles that guide every product and hire.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div
                key={v.title}
                className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                  <Icon name={v.icon} className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-slate-900">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="numbers-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="numbers-heading" className="section-title">By the numbers</h2>
            <p className="section-subtitle mx-auto">Traction from teams that trust us.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {customerStats.map((s) => (
              <div
                key={s.label}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-6 text-center shadow-sm"
              >
                <div className="text-3xl font-extrabold text-brand-700 sm:text-4xl">{s.value}</div>
                <div className="mt-1 text-sm font-bold text-slate-900">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-20 text-white" aria-label="Work with us">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Shape the future of CX</h2>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-slate-300">
            We hire people who care about craft, customers, and moving fast.
          </p>
          <Link
            to="/careers"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-base font-bold text-slate-900 transition hover:bg-slate-100"
          >
            View open roles <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
